import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "../api/client";

const CSS = `
.ins-grid4 { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; margin-bottom:20px; }
@media(max-width:900px){ .ins-grid4{ grid-template-columns:repeat(2,1fr); } }
@media(max-width:500px){ .ins-grid4{ grid-template-columns:1fr; } }
.ins-card { background:#ffffff; border:1px solid #c0d0eb; border-radius:10px; padding:20px; }
.ins-card-title { font-size:12px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; color:#6080aa; margin-bottom:14px; }
.ins-rank-row { display:flex; align-items:center; gap:8px; padding:6px 0; border-bottom:1px solid #d5e3f5; }
.ins-rank-row:last-child { border-bottom:none; }
.ins-rank-num  { font-size:11px; color:#6080aa; width:18px; flex-shrink:0; text-align:right; font-variant-numeric:tabular-nums; }
.ins-rank-name { font-size:13px; color:#0d1a36; flex:1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.ins-rank-val  { font-size:13px; font-weight:600; flex-shrink:0; font-variant-numeric:tabular-nums; }
.ins-empty { font-size:12px; color:#6080aa; padding:8px 0; }

.ins-section { background:#ffffff; border:1px solid #c0d0eb; border-radius:10px; padding:24px; margin-bottom:16px; }
.ins-section-title { font-size:13px; font-weight:700; color:#0d1a36; margin-bottom:16px; }
.ins-search-row { display:flex; gap:10px; margin-bottom:16px; }
.ins-input { background:#f4f8ff; border:1px solid #d5e3f5; border-radius:7px; padding:9px 12px;
  font-size:13px; color:#0d1a36; outline:none; font-family:inherit; flex:1;
  transition:border-color .15s; }
.ins-input:focus { border-color:#059669; }
.ins-chips { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:12px; }
.ins-chip { padding:5px 12px; border-radius:20px; font-size:12px; font-weight:500;
  background:#f4f8ff; border:1px solid #c0d0eb; color:#2a4a78; cursor:default; }
.ins-chip-best { background:rgba(16,185,129,.12); border-color:rgba(16,185,129,.3); color:#059669; font-weight:700; }
.ins-cheapest { font-size:13px; color:#059669; margin-bottom:12px; }
.ins-chart-label { font-size:11px; color:#2a4a78; margin-bottom:8px; }

.ins-spinner-wrap { display:flex; justify-content:center; align-items:center; height:200px; }
.ins-spinner { width:32px; height:32px; border:3px solid #c0d0eb; border-top-color:#059669;
  border-radius:50%; animation:ins-spin .6s linear infinite; }
@keyframes ins-spin { to{ transform:rotate(360deg); } }
`;

function RankCard({ title, rows, valueKey, labelKey, color, prefix="", currencyKey=null }) {
  return (
    <div className="ins-card">
      <div className="ins-card-title">{title}</div>
      {rows.length === 0
        ? <div className="ins-empty">No data yet</div>
        : rows.slice(0, 8).map((r, i) => {
          const pfx = currencyKey ? `${r[currencyKey]||"OMR"} ` : prefix;
          return (
            <div className="ins-rank-row" key={i}>
              <span className="ins-rank-num">{i+1}</span>
              <span className="ins-rank-name">{r[labelKey]}</span>
              <span className="ins-rank-val" style={{color}}>
                {pfx}{parseFloat(r[valueKey]||0).toFixed(pfx?"3":"0")}{!pfx?"×":""}
              </span>
            </div>
          );
        })
      }
    </div>
  );
}

export default function Insights() {
  const [mostPurchased, setMostPurchased] = useState([]);
  const [mostExpensive, setMostExpensive] = useState([]);
  const [freqShops,     setFreqShops]     = useState([]);
  const [catSpend,      setCatSpend]      = useState([]);
  const [itemNames,     setItemNames]     = useState([]);
  const [selectedItem,  setSelectedItem]  = useState("");
  const [shopPrices,    setShopPrices]    = useState([]);
  const [priceHistory,  setPriceHistory]  = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/expense/insights/most-purchased"),
      api.get("/expense/insights/most-expensive"),
      api.get("/expense/insights/frequent-shops"),
      api.get("/expense/insights/category-spending"),
      api.get("/expense/insights/item-names"),
    ]).then(([mp, me, fs, cs, nm]) => {
      setMostPurchased(mp.data);
      setMostExpensive(me.data);
      setFreqShops(fs.data);
      setCatSpend(cs.data);
      setItemNames(nm.data.map(n => n.item_name || n));
    }).catch(e => {
      setError(e?.message || "Failed to load insights. Check backend is running.");
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedItem) { setShopPrices([]); setPriceHistory([]); return; }
    Promise.all([
      api.get(`/expense/insights/price-by-shop?item_name=${encodeURIComponent(selectedItem)}`),
      api.get(`/expense/insights/price-history?item_name=${encodeURIComponent(selectedItem)}`),
    ]).then(([sp, ph]) => {
      setShopPrices(sp.data); setPriceHistory(ph.data);
    }).catch(() => {});
  }, [selectedItem]);

  const cheapest = shopPrices.length
    ? shopPrices.reduce((a, b) => a.avg_price < b.avg_price ? a : b)
    : null;

  const filtered = selectedItem
    ? itemNames.filter(n => n.toLowerCase().includes(selectedItem.toLowerCase())).slice(0,8)
    : [];

  return (
    <>
      <style>{CSS}</style>
      <div style={{fontSize:18,fontWeight:700,marginBottom:24,color:'#0d1a36'}}>💡 Insights</div>

      {error && (
        <div style={{padding:'12px 16px',borderRadius:8,background:'rgba(248,113,113,.08)',
                     border:'1px solid rgba(248,113,113,.2)',color:'#dc2626',fontSize:13,marginBottom:20}}>
          ⚠ {error}
        </div>
      )}

      {loading
        ? <div className="ins-spinner-wrap"><div className="ins-spinner"/></div>
        : (
          <>
            <div className="ins-grid4">
              <RankCard title="🛒 Most Purchased" rows={mostPurchased}
                        labelKey="item_name" valueKey="times_purchased" color="#0284c7" />
              {/* Single most expensive item card */}
              <div className="ins-card">
                <div className="ins-card-title">💰 MOST EXPENSIVE</div>
                {mostExpensive.length === 0
                  ? <div className="ins-empty">No data yet</div>
                  : (() => { const top = mostExpensive[0]; return (
                    <div style={{textAlign:'center',padding:'12px 0'}}>
                      <div style={{fontSize:11,color:'#6080aa',fontWeight:600,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:8}}>Most Expensive Item</div>
                      <div style={{fontSize:15,fontWeight:700,color:'#0d1a36',marginBottom:10,wordBreak:'break-word'}}>{top.item_name}</div>
                      <div style={{fontSize:28,fontWeight:800,color:'#dc2626',fontVariantNumeric:'tabular-nums'}}>{top.currency||"OMR"} {parseFloat(top.avg_price||0).toFixed(3)}</div>
                    </div>
                  ); })()
                }
              </div>
              <RankCard title="🏪 Top Shops" rows={freqShops}
                        labelKey="shop_name" valueKey="visit_count" color="#7c3aed" />
              <RankCard title="📂 Category Spend" rows={catSpend}
                        labelKey="category" valueKey="total" color="#059669" currencyKey="currency" />
            </div>

            <div className="ins-section">
              <div className="ins-section-title">🔍 Price Comparison by Item</div>
              <div className="ins-search-row">
                <input className="ins-input" placeholder="Type item name to compare prices…"
                       value={selectedItem} onChange={e => setSelectedItem(e.target.value)} />
              </div>

              {/* Autocomplete suggestions */}
              {filtered.length > 0 && !shopPrices.length && (
                <div className="ins-chips">
                  {filtered.map(n => (
                    <span key={n} className="ins-chip" style={{cursor:'pointer'}}
                          onClick={() => setSelectedItem(n)}>{n}</span>
                  ))}
                </div>
              )}

              {shopPrices.length > 0 && (
                <>
                  <div className="ins-chips">
                    {shopPrices.map(s => (
                      <span key={s.shop_name}
                            className={`ins-chip${cheapest?.shop_name===s.shop_name?" ins-chip-best":""}`}>
                        {s.shop_name} — {s.currency||"OMR"} {parseFloat(s.avg_price).toFixed(3)}
                      </span>
                    ))}
                  </div>
                  {cheapest && (
                    <div className="ins-cheapest">
                      ✅ Cheapest at <strong>{cheapest.shop_name}</strong> — {cheapest.currency||"OMR"} {parseFloat(cheapest.avg_price).toFixed(3)}
                    </div>
                  )}
                </>
              )}

              {priceHistory.length > 0 && (
                <>
                  <div className="ins-chart-label">Price history for "{selectedItem}"</div>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={priceHistory}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#c0d0eb" />
                      <XAxis dataKey="date" stroke="#6080aa" tick={{fontSize:11}}
                             tickFormatter={d => d?.slice(5)} />
                      <YAxis stroke="#6080aa" tick={{fontSize:11}} />
                      <Tooltip contentStyle={{background:'#ffffff',border:'1px solid #c0d0eb',color:'#0d1a36'}}
                               formatter={(v,n,props) => [`${props?.payload?.currency||"OMR"} ${parseFloat(v).toFixed(3)}`,"Price"]} />
                      <Line type="monotone" dataKey="price" stroke="#059669" dot={false} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </>
              )}
            </div>
          </>
        )
      }
    </>
  );
}
